//* libraries
import { createTheme, ThemeProvider as MuiThemeProvider } from '@material-ui/core'
import Cookies from 'js-cookie'
import React, { FC, useEffect, useState } from 'react'
import { ThemeProvider } from 'styled-components'

//* components
import Helmet from 'react-helmet'
import Announcement from '../global/announcement'
import LocalizedBanner from '../global/localizedBanner'
import RenderFooter from './renderFooter'
import RenderHeader from './renderHeader'

//* styles
import { GlobalStyle } from '../../styles/global'
import palette from '../../styles/palette'
import typography from '../../styles/typography'

//* utils
import { ComponentCustomNav } from '../../models/components/customNav'
import { ComponentMainNavBanner } from '../../models/components/mainNavBanner'
import { getHostName } from '../../utils/host.utils'

const customizedTheme = createTheme({
  // @ts-ignore
  webMktPalette: palette,
  webMktTypography: typography,
  breakpoints: {
    values: {
      xs: 0,
      sm: 769,
      md: 1025,
      lg: 1281,
      xl: 1600,
    },
  },
})

type Props = {
  children: any
  lang: string
  hideNormalLayout?: boolean
  customNav?: ComponentCustomNav | null
  minimalHeader?: boolean
  minimalFooter?: boolean
  headerData?: any
  footerData?: any
  showHeader?: boolean
  showFooter?: boolean
  announcementData?: any
  mainNavBannerData?: ComponentMainNavBanner | undefined
  browserLanguageData?: any
  slug?: string
  pageType?: string
  availableLocales?: any
}

const Template: FC<Props> = ({
  children,
  lang,
  hideNormalLayout,
  customNav,
  minimalHeader,
  minimalFooter,
  headerData,
  footerData,
  showHeader = true,
  showFooter = true,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
  slug,
  pageType,
  availableLocales,
}) => {
  const [browserLanguage, setBrowserLanguage] = useState('')
  const [currentSlug, setCurrentSlug] = useState(slug)

  useEffect(() => {
    if (typeof navigator === 'object') {
      const browserLanguage = (navigator.languages && navigator.languages[0]) || navigator.language
      setBrowserLanguage(browserLanguage)
    }

    const hostname = window.location.hostname
    oneTrustCustomWinLoad(function () {
      const newScriptElement = document.createElement('script')
      newScriptElement.type = 'text/javascript'
      newScriptElement.innerHTML = `
        window.optimizely = window.optimizely || [];
        window.optimizely.push({
          type: 'holdEvents',
        });
      `
      document.head.appendChild(newScriptElement)

      if (hostname === 'www.uipath.com')
        loadOneTrustAndOptimisely(500, '998cf2d3-b73b-4e35-a85c-dadf7e609fb8')
      else if (hostname !== 'www.uipath.com.cn')
        loadOneTrustAndOptimisely(500, 'f152921e-2c10-49f2-8d23-c5de87676b02')
    })
  }, [])

  useEffect(() => {
    const script = document.createElement('script')
    script.src = '/steam-data/des_carry_v_111_min.js'
    script.async = true
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])

  useEffect(() => {
    setCurrentSlug(slug)
  }, [slug, setCurrentSlug])

  //* Common code for execution of hash URLs
  useEffect(() => {
    setLocaleCookie(7)
    const hashId = window.location.hash.slice(1)
    const element = document.getElementById(hashId)

    if (element) {
      setTimeout(function () {
        const scrollDiv = (element?.offsetTop || 100) - 100
        window.scrollTo({ top: scrollDiv, behavior: 'smooth' })
      }, 1000)
    }
  }, [])

  function setLocaleCookie(numOfDays: number) {
    const host = getHostName()

    //* removes the www from the domain
    let domain = window.location.hostname.split('www').join('')
    if (domain.substring(0, 1) !== '.') domain = `.${domain}`

    Cookies.set(`locale-${host}`, lang, { domain, expires: numOfDays })
  }

  //* OneTrust Consent Manager
  const oneTrustCustomWinLoad = (callback: any) => {
    if (document.readyState === 'complete') {
      callback()
    } else {
      window.addEventListener('load', callback)
    }
  }

  const loadOneTrustAndOptimisely = (extraDelayMS: any, onetrustId: string) => {
    //* Initialization of OneTrust Consent Manager
    setTimeout(function () {
      // Purposfully commented out as it is not needed for now.
      // OneTrust Script
      // var s = document.createElement('script')
      // s.type = 'text/javascript'
      // s.charset = 'UTF-8'
      // s.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
      // s.async = true
      // s.setAttribute('data-domain-script', onetrustId)
      // s.setAttribute('nonce', '{{nonce}}')

      let i = document.getElementsByTagName('script')[0]
      //i?.parentNode?.insertBefore(s, i)

      var s2 = document.createElement('script')
      s2.type = 'text/javascript'
      s2.setAttribute('nonce', '{{nonce}}')
      s2.innerHTML = `function OptanonWrapper() { 
          window.dataLayer.push( { event: 'OneTrustGroupsUpdated'} );
          const cookieLevel = '002';
          if (OptanonActiveGroups.indexOf(cookieLevel) > -1) {
            window.optimizely = window.optimizely || [];
            window["optimizely"].push({
              "type": "sendEvents"
            });
          }
        }`
      i?.parentNode?.insertBefore(s2, i)
    }, extraDelayMS)
  }

  return (
    <div data-cy="Layout" lang={lang}>
      <MuiThemeProvider theme={customizedTheme}>
        <ThemeProvider theme={customizedTheme}>
          <GlobalStyle $langCode={lang} />

          <Helmet>
            <link
              rel="stylesheet"
              href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100;300;400;500;700&family=Noto+Sans+KR:wght@100;300;400;500;700&family=Noto+Sans+TC:wght@100;300;400;500;600;700;800;900&display=swap"
            />
            <link
              rel="stylesheet"
              href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
            />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin={'anonymous'} />
            <link
              rel="preconnect"
              href="https://hubspot-cdn.uipath.com"
              crossOrigin={'anonymous'}
            />

            <link
              rel="preconnect"
              href="https://www.googletagmanager.com"
              crossOrigin={'anonymous'}
            />

            <link
              rel="stylesheet"
              type="text/css"
              href="//cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.css"
            />
            <link
              rel="stylesheet"
              type="text/css"
              href="//cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.8.1/slick-theme.min.css"
            />
          </Helmet>

          <LocalizedBanner
            slug={currentSlug || '/'}
            defaultBrowserLanguage={browserLanguage}
            browserLanguageData={browserLanguageData}
            availableLocales={availableLocales}
          />

          <Announcement locale={lang} announcementData={announcementData} page={pageType} />

          {!hideNormalLayout && showHeader && (
            <RenderHeader
              headerData={headerData}
              mainNavBannerData={mainNavBannerData}
              minimalHeader={minimalHeader}
              customNav={customNav}
              locale={lang}
            />
          )}

          {children}

          {!hideNormalLayout && showFooter && (
            <RenderFooter minimalFooter={minimalFooter} footerData={footerData} locale={lang} />
          )}
        </ThemeProvider>
      </MuiThemeProvider>
    </div>
  )
}

export default { Template }
